import { defineStore } from 'pinia';
import { ref } from 'vue';
import axios from 'axios';
import { ProjectService, type ProjectListParams } from '@/shared/services/projects';
import { mapProjectStatusToApi, projectMapper, type UiProject } from '@/shared/mappers';
import { sieve } from '@/shared/utils/sieve';
import { ProjectStatus } from '@/core/types/models/Project';

export const useProjectStore = defineStore('project', () => {
  const projects = ref<UiProject[]>([]);
  // Use Pinia persisted state for selectedProjectId
  const selectedProjectId = ref<string | null>(null);
  const search = ref('');
  const projectCache = ref<Record<string, UiProject>>({});

  // Pagination state
  const page = ref(1);
  const pageSize = ref(6); // Default page size
  const totalProjects = ref(0); // For API, set by response

  // Loading state
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  // Auto-initialization flag to prevent multiple initialization calls
  const isInitialized = ref(false);

  // Cache validation flag (in-memory only) to avoid repeated calls after login
  const hasValidatedSelection = ref(false);

  function resetSelectionValidation(): void {
    hasValidatedSelection.value = false;
  }

  function clearSelectedProjectCache(): void {
    selectedProjectId.value = null;
    projectCache.value = {};
    isInitialized.value = false;
    resetSelectionValidation();
  }

  function cacheProjects(items: UiProject[]): void {
    if (items.length === 0) {
      return;
    }

    const updatedCache: Record<string, UiProject> = { ...projectCache.value };
    items.forEach(project => {
      if (project.id) {
        updatedCache[project.id] = project;
      }
    });

    projectCache.value = updatedCache;
  }

  async function validateSelectedProjectExists(): Promise<boolean> {
    if (hasValidatedSelection.value) {
      return selectedProjectId.value !== null;
    }

    const candidateProjectId = selectedProjectId.value;
    if (!candidateProjectId) {
      hasValidatedSelection.value = true;
      return false;
    }

    try {
      const projectDto = await ProjectService.getProject(candidateProjectId);
      const project = projectMapper.mapToUiModel(projectDto);
      cacheProjects([{ ...project, id: project.id || candidateProjectId }]);
      hasValidatedSelection.value = true;
      return true;
    } catch (err: unknown) {
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;

      if (status === 403 || status === 404 || status === 410) {
        console.warn(`Selected project '${candidateProjectId}' is no longer accessible (${status}). Clearing cache.`);
        clearSelectedProjectCache();
        hasValidatedSelection.value = true;
        return false;
      }

      // For other errors, don't destroy selection; allow app-level handling.
      hasValidatedSelection.value = true;
      return true;
    }
  }

  // Initialize store - hydrate cached selection and ensure we have corresponding project data
  async function initialize(): Promise<void> {
    if (isInitialized.value) return;

    const hasCachedSelection = Boolean(selectedProjectId.value);
    const needsProjectFetch = projects.value.length === 0 && hasCachedSelection;

    if (needsProjectFetch) {
      await fetchProjects();
    }

    if (projects.value.length > 0) {
      // After ensuring we have project data, validate cached selections.
      validateAndCleanupCache();
    }

    isInitialized.value = true;
  }

  // Fetch all projects from API (API handles filtering by user role)
  async function fetchProjects(
    { page: pageNum = 1, pageSize: size = 5, search: searchTerm = '', status = '' }: {
      page?: number;
      pageSize?: number;
      search?: string;
      status?: string;
    } = {}
  ) {
    isLoading.value = true;
    error.value = null;
    
    try {
      const normalizedStatus = status?.trim() ?? '';

      // Build Sieve query
      const queryBuilder = sieve()
        .page(pageNum)
        .pageSize(size);

      // Add search filter if provided
      if (searchTerm.trim()) {
        queryBuilder.search('name', searchTerm.trim());
      }

      // Add status filter if provided - convert UI status to API numeric value
      if (normalizedStatus) {
        const apiStatusValue = mapProjectStatusToApi(normalizedStatus);
        if (apiStatusValue !== undefined) {
          queryBuilder.filter('status', '==', apiStatusValue);
        }
      } else {
        const pendingStatusFilter = mapProjectStatusToApi(ProjectStatus.Pending);
        if (pendingStatusFilter !== undefined) {
          queryBuilder.filter('status', '!=', pendingStatusFilter);
        } else {
          console.warn('Pending project status mapping missing; pending projects will not be excluded.');
        }
      }

      const params: ProjectListParams = queryBuilder.build();
      
      const result = await ProjectService.getMyProjects(params);
      
      // Use mapper to convert DTOs to UI models
      const projectDtos = result.data || [];
      const uiProjects = projectMapper.mapToUiModels(projectDtos);
      projects.value = uiProjects;
      cacheProjects(uiProjects);
      
      // Extract pagination info
      const paginationInfo = projectMapper.mapPaginationInfo(result.pagination);
      totalProjects.value = paginationInfo.totalItems;
      page.value = paginationInfo.currentPage;
      pageSize.value = paginationInfo.pageSize;
      search.value = searchTerm;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch projects';
      projects.value = [];
      totalProjects.value = 0;
    } finally {
      isLoading.value = false;
    }
  }

  async function selectProject(id: string): Promise<void> {
    const previousProjectId = selectedProjectId.value;
    selectedProjectId.value = id;
    resetSelectionValidation();

    // If this is a different project, clear asset data
    if (previousProjectId !== id) {
      // Clear asset stores when project changes - use dynamic imports to avoid circular dependencies
      try {
        const [assetTreeModule, assetHierarchyModule] = await Promise.all([
          import('@/shared/stores/assetTree'),
          import('@/shared/stores/assetHierarchy')
        ]);
        const assetTreeStore = assetTreeModule.useAssetTreeStore();
        const assetHierarchyStore = assetHierarchyModule.useAssetHierarchyStore();

        assetTreeStore.resetProjectData();
        assetHierarchyStore.resetProjectData();
      } catch (error: unknown) {
        console.warn('Failed to reset asset stores:', error);
      }
    }
  }

  function setSearch(val: string) {
    search.value = val;
  }

  // Validate cached selectedProjectId against the fetched project list
  function validateAndCleanupCache(): void {
    const projectIds = new Set(Object.keys(projectCache.value));
    
    // If selectedProjectId doesn't exist in current projects, invalidate it
    if (selectedProjectId.value && !projectIds.has(selectedProjectId.value)) {
      console.warn(`Selected project '${selectedProjectId.value}' no longer exists. Clearing cache.`);
      selectedProjectId.value = null;
      resetSelectionValidation();
    }
  }

  // Invalidate all cache (for manual cache clearing)
  function invalidateCache(): void {
    clearSelectedProjectCache();
    console.info('Project cache invalidated manually.');
  }


  // Filtered projects by search (for local mock, but API will handle this)
  const filteredProjects = () => {
    return projects.value;
  };

  function getProjectById(id: string | null | undefined): UiProject | null {
    if (!id) {
      return null;
    }
    return projectCache.value[id] ?? null;
  }

  return {
    projects,
    selectedProjectId,
    search,
    selectProject,
    setSearch,
    filteredProjects,
    // Pagination
    page,
    pageSize,
    totalProjects,
    fetchProjects,
    initialize,
    isLoading,
    error,
    validateSelectedProjectExists,
    resetSelectionValidation,
    clearSelectedProjectCache,
    // Cache validation and cleanup
    validateAndCleanupCache,
    invalidateCache,
    getProjectById,
  };
}, {
  persist: [
    {
      storage: sessionStorage,
      pick: ['selectedProjectId'],
    },
  ],
});
